import React, { useState, useEffect, useCallback } from 'react';
import { confirmDialog } from '../ConfirmDialog';
import { useQuery } from '@tanstack/react-query';
import { X, Plus, Trash2, Search, Loader2 } from 'lucide-react';
import { loggedGet, apiClient } from './loggedFetch';

const INITIATIVE_TYPES = [
  { value: 'takeout', label: 'Takeout' },
  { value: 'expansion', label: 'Expansion' },
  { value: 'defense', label: 'Defense' },
  { value: 'custom', label: 'Custom' },
];

const STATUSES = [
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'abandoned', label: 'Abandoned' },
];

const COLORS = [
  '#2196F3', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7',
  '#ec4899', '#ef4444', '#f97316', '#eab308', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#64748b',
];

function generateId() {
  return `ms-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

function formatCurrency(num) {
  if (num == null) return '';
  const n = Number(num) || 0;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n)}`;
}

function InitiativeModal({ isOpen, onClose, initiative, vendor, onSave, onDelete }) {
  const isEditing = !!initiative?.id;

  // Form state
  const [activeTab, setActiveTab] = useState('details');
  const [form, setForm] = useState({
    title: '',
    description: '',
    initiative_type: 'takeout',
    status: 'planning',
    target_value: '',
    actual_value: '',
    start_date: '',
    target_end_date: '',
    actual_end_date: '',
    notes: '',
    color: '#2196F3',
    milestones: [],
    linked_renewals: [],
  });
  const [saving, setSaving] = useState(false);
  const [newMilestone, setNewMilestone] = useState({ title: '', target_date: '' });
  const [renewalSearch, setRenewalSearch] = useState('');

  // Initialize form when initiative changes
  useEffect(() => {
    if (initiative) {
      setForm({
        title: initiative.title || '',
        description: initiative.description || '',
        initiative_type: initiative.initiative_type || 'takeout',
        status: initiative.status || 'planning',
        target_value: initiative.target_value?.toString() || '',
        actual_value: initiative.actual_value?.toString() || '',
        start_date: initiative.start_date?.split('T')[0] || '',
        target_end_date: initiative.target_end_date?.split('T')[0] || '',
        actual_end_date: initiative.actual_end_date?.split('T')[0] || '',
        notes: initiative.notes || '',
        color: initiative.color || '#2196F3',
        milestones: initiative.milestones || [],
        linked_renewals: initiative.linked_renewals || [],
      });
    } else {
      // Reset for new initiative
      setForm({
        title: '',
        description: '',
        initiative_type: 'takeout',
        status: 'planning',
        target_value: '',
        actual_value: '',
        start_date: '',
        target_end_date: '',
        actual_end_date: '',
        notes: '',
        color: '#2196F3',
        milestones: [],
        linked_renewals: [],
      });
    }
    setActiveTab('details');
  }, [initiative, isOpen]);

  // Fetch available renewals for linking
  const { data: renewalsData, isLoading: renewalsLoading } = useQuery({
    queryKey: ['vendor-analysis', 'renewals', vendor, renewalSearch],
    queryFn: loggedGet(
      'InitiativeModal:renewals',
      `/api/vendor-analysis/${encodeURIComponent(vendor)}/renewals?limit=20&search=${encodeURIComponent(renewalSearch)}`
    ),
    enabled: isOpen && activeTab === 'renewals' && !!vendor,
    staleTime: 30000,
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddMilestone = () => {
    if (!newMilestone.title.trim()) return;
    const milestone = {
      id: generateId(),
      title: newMilestone.title.trim(),
      target_date: newMilestone.target_date || null,
      completed_at: null,
    };
    setForm(prev => ({
      ...prev,
      milestones: [...prev.milestones, milestone],
    }));
    setNewMilestone({ title: '', target_date: '' });
  };

  const handleToggleMilestone = (id) => {
    setForm(prev => ({
      ...prev,
      milestones: prev.milestones.map(m =>
        m.id === id
          ? { ...m, completed_at: m.completed_at ? null : new Date().toISOString() }
          : m
      ),
    }));
  };

  const handleDeleteMilestone = (id) => {
    setForm(prev => ({
      ...prev,
      milestones: prev.milestones.filter(m => m.id !== id),
    }));
  };

  const handleLinkRenewal = (renewal) => {
    // Check if already linked
    const alreadyLinked = form.linked_renewals.some(
      r => r.purchase_document_num === renewal.purchase_document_num
    );
    if (alreadyLinked) return;

    const linked = {
      purchase_document_num: renewal.purchase_document_num,
      department_id: renewal.department_id,
      agency: renewal.agency_name || renewal.agency,
      end_date: renewal.end_date,
      value: renewal.total_value || renewal.value,
    };
    setForm(prev => ({
      ...prev,
      linked_renewals: [...prev.linked_renewals, linked],
    }));
  };

  const handleUnlinkRenewal = (poNum) => {
    setForm(prev => ({
      ...prev,
      linked_renewals: prev.linked_renewals.filter(
        r => r.purchase_document_num !== poNum
      ),
    }));
  };

  const handleSave = useCallback(async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        initiative_type: form.initiative_type,
        status: form.status,
        target_value: form.target_value ? parseFloat(form.target_value) : null,
        actual_value: form.actual_value ? parseFloat(form.actual_value) : null,
        start_date: form.start_date || null,
        target_end_date: form.target_end_date || null,
        actual_end_date: form.actual_end_date || null,
        notes: form.notes.trim() || null,
        color: form.color,
        milestones: form.milestones,
        linked_renewals: form.linked_renewals,
      };
      await onSave(payload, initiative?.id);
      onClose();
    } finally {
      setSaving(false);
    }
  }, [form, initiative, onSave, onClose]);

  const handleDelete = useCallback(async () => {
    if (!initiative?.id) return;
    if (!(await confirmDialog('Delete this initiative? This cannot be undone.'))) return;
    setSaving(true);
    try {
      await onDelete(initiative.id);
      onClose();
    } finally {
      setSaving(false);
    }
  }, [initiative, onDelete, onClose]);

  if (!isOpen) return null;

  const availableRenewals = (renewalsData?.renewals || []).filter(
    r => !form.linked_renewals.some(
      lr => lr.purchase_document_num === r.purchase_document_num
    )
  );

  return (
    <div className="si-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="si-modal" onClick={(e) => e.stopPropagation()}>
        <div className="si-modal__header">
          <h2 className="si-modal__title">
            {isEditing ? 'Edit Initiative' : 'New Initiative'}
          </h2>
          <button className="si-modal__close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="si-modal__tabs">
          <button
            className={`si-modal__tab ${activeTab === 'details' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          <button
            className={`si-modal__tab ${activeTab === 'milestones' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('milestones')}
          >
            Milestones ({form.milestones.length})
          </button>
          <button
            className={`si-modal__tab ${activeTab === 'renewals' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('renewals')}
          >
            Linked Renewals ({form.linked_renewals.length})
          </button>
        </div>

        <div className="si-modal__body">
          {activeTab === 'details' && (
            <>
              <div className="si-form-group">
                <label className="si-form-label">Title *</label>
                <input
                  type="text"
                  className="si-form-input"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., SOAR Takeout Q2 2026"
                />
              </div>

              <div className="si-form-group">
                <label className="si-form-label">Description</label>
                <textarea
                  className="si-form-textarea"
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Strategic context and objectives..."
                />
              </div>

              <div className="si-form-row">
                <div className="si-form-group">
                  <label className="si-form-label">Type *</label>
                  <select
                    className="si-form-select"
                    value={form.initiative_type}
                    onChange={(e) => handleChange('initiative_type', e.target.value)}
                  >
                    {INITIATIVE_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="si-form-group">
                  <label className="si-form-label">Status</label>
                  <select
                    className="si-form-select"
                    value={form.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                  >
                    {STATUSES.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="si-form-row">
                <div className="si-form-group">
                  <label className="si-form-label">Target Value ($)</label>
                  <input
                    type="number"
                    className="si-form-input"
                    value={form.target_value}
                    onChange={(e) => handleChange('target_value', e.target.value)}
                    placeholder="500000"
                  />
                </div>
                <div className="si-form-group">
                  <label className="si-form-label">Actual Value ($)</label>
                  <input
                    type="number"
                    className="si-form-input"
                    value={form.actual_value}
                    onChange={(e) => handleChange('actual_value', e.target.value)}
                    placeholder="Realized revenue"
                  />
                </div>
              </div>

              <div className="si-form-row">
                <div className="si-form-group">
                  <label className="si-form-label">Start Date</label>
                  <input
                    type="date"
                    className="si-form-input"
                    value={form.start_date}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                  />
                </div>
                <div className="si-form-group">
                  <label className="si-form-label">Target End Date</label>
                  <input
                    type="date"
                    className="si-form-input"
                    value={form.target_end_date}
                    onChange={(e) => handleChange('target_end_date', e.target.value)}
                  />
                </div>
              </div>

              <div className="si-form-group">
                <label className="si-form-label">Notes</label>
                <textarea
                  className="si-form-textarea"
                  value={form.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  placeholder="Account intel, stakeholders, blockers..."
                />
              </div>

              <div className="si-form-group">
                <label className="si-form-label">Color</label>
                <div className="si-color-picker">
                  {COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      className={`si-color-swatch ${form.color === c ? 'is-selected' : ''}`}
                      style={{ background: c }}
                      onClick={() => handleChange('color', c)}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {activeTab === 'milestones' && (
            <>
              <div className="si-milestones">
                {form.milestones.length === 0 ? (
                  <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
                    No milestones yet. Add key checkpoints to track progress.
                  </p>
                ) : (
                  form.milestones.map(m => (
                    <div key={m.id} className="si-milestone-item">
                      <input
                        type="checkbox"
                        className="si-milestone-item__checkbox"
                        checked={!!m.completed_at}
                        onChange={() => handleToggleMilestone(m.id)}
                      />
                      <span
                        className="si-milestone-item__title"
                        style={{
                          textDecoration: m.completed_at ? 'line-through' : 'none',
                          opacity: m.completed_at ? 0.6 : 1,
                        }}
                      >
                        {m.title}
                      </span>
                      {m.target_date && (
                        <span className="si-milestone-item__date">
                          {new Date(m.target_date).toLocaleDateString()}
                        </span>
                      )}
                      <button
                        className="si-milestone-item__delete"
                        onClick={() => handleDeleteMilestone(m.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="si-add-milestone">
                <input
                  type="text"
                  className="si-form-input"
                  placeholder="Milestone title..."
                  value={newMilestone.title}
                  onChange={(e) => setNewMilestone(p => ({ ...p, title: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddMilestone()}
                />
                <input
                  type="date"
                  className="si-form-input"
                  style={{ width: '160px', flexShrink: 0 }}
                  value={newMilestone.target_date}
                  onChange={(e) => setNewMilestone(p => ({ ...p, target_date: e.target.value }))}
                />
                <button
                  type="button"
                  className="si-btn si-btn--primary"
                  onClick={handleAddMilestone}
                  disabled={!newMilestone.title.trim()}
                >
                  <Plus size={16} />
                </button>
              </div>
            </>
          )}

          {activeTab === 'renewals' && (
            <div className="si-renewals-section">
              <div className="si-renewal-search">
                <div style={{ position: 'relative', flex: 1 }}>
                  <Search
                    size={16}
                    style={{
                      position: 'absolute',
                      left: 12,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--color-text-muted)',
                    }}
                  />
                  <input
                    type="text"
                    className="si-form-input"
                    style={{ paddingLeft: 36 }}
                    placeholder="Search renewals by agency..."
                    value={renewalSearch}
                    onChange={(e) => setRenewalSearch(e.target.value)}
                  />
                </div>
              </div>

              {form.linked_renewals.length > 0 && (
                <>
                  <label className="si-form-label" style={{ marginTop: 'var(--space-3)' }}>
                    Linked Renewals
                  </label>
                  <div className="si-linked-renewals">
                    {form.linked_renewals.map(r => (
                      <div key={r.purchase_document_num} className="si-renewal-item">
                        <div className="si-renewal-item__info">
                          <div className="si-renewal-item__agency">{r.agency}</div>
                          <div className="si-renewal-item__meta">
                            {r.purchase_document_num}
                            {r.end_date && ` · Ends ${new Date(r.end_date).toLocaleDateString()}`}
                            {r.value && ` · ${formatCurrency(r.value)}`}
                          </div>
                        </div>
                        <button
                          className="si-renewal-item__unlink"
                          onClick={() => handleUnlinkRenewal(r.purchase_document_num)}
                        >
                          Unlink
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {renewalsLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 16, color: 'var(--color-text-muted)' }}>
                  <Loader2 size={16} className="vca-spin" />
                  Loading renewals...
                </div>
              ) : availableRenewals.length > 0 ? (
                <>
                  <label className="si-form-label" style={{ marginTop: 'var(--space-3)' }}>
                    Available Renewals
                  </label>
                  <div className="si-linked-renewals">
                    {availableRenewals.slice(0, 10).map(r => (
                      <div key={r.purchase_document_num} className="si-renewal-item">
                        <div className="si-renewal-item__info">
                          <div className="si-renewal-item__agency">{r.agency_name || r.agency}</div>
                          <div className="si-renewal-item__meta">
                            {r.purchase_document_num}
                            {r.end_date && ` · Ends ${new Date(r.end_date).toLocaleDateString()}`}
                            {(r.total_value || r.value) && ` · ${formatCurrency(r.total_value || r.value)}`}
                          </div>
                        </div>
                        <button
                          className="si-btn si-btn--secondary"
                          style={{ padding: '6px 12px', minHeight: 'auto', fontSize: '0.8rem' }}
                          onClick={() => handleLinkRenewal(r)}
                        >
                          Link
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              ) : renewalSearch ? (
                <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', marginTop: 'var(--space-3)' }}>
                  No renewals found matching "{renewalSearch}"
                </p>
              ) : null}
            </div>
          )}
        </div>

        <div className="si-modal__footer">
          {isEditing && (
            <button
              type="button"
              className="si-btn si-btn--danger"
              onClick={handleDelete}
              disabled={saving}
              style={{ marginRight: 'auto' }}
            >
              Delete
            </button>
          )}
          <button
            type="button"
            className="si-btn si-btn--secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="si-btn si-btn--primary"
            onClick={handleSave}
            disabled={saving || !form.title.trim()}
          >
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Initiative'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default InitiativeModal;
